import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { resolveDeskSlaStatus } from "../../../../utils/deskSlaStatus";
import { DsBadge } from "../../../../design-system";
import {
  getChecklistNameHighlight,
  getDeskActionButtons,
  resolveDeskActionKind,
  formatDeskDisplayDate,
  formatDeskDisplayDateTime,
} from "../../../../utils/deskActionMatrix";
import {
  ONBOARDING_APPROVAL_LABEL,
  ONBOARDING_REQUEST_LABEL,
} from "../../../../utils/onboardingApprovalDesk";
import {
  deskRequestIdsEqual,
  ensureUniqueDeskRequests,
  findDeskRequest,
  normalizeDeskRequestId,
} from "../../../../utils/deskRowIdentity";
import { sortDeskRequestsLatestFirst } from "../../../../utils/deskListOrder";
import {
  resolveWorkflowSteps,
  workflowPendingDisplay,
} from "../../../../utils/workflowStepsBadge";
import { isLeaveDeskRequest, isLeaveActionLabel, leaveLinkedCancellationCaption } from "../../../../utils/leaveRequestDesk";
import LeaveDeskTrackingLayout, {
  DeskRequestTrackingLayout,
} from "./LeaveDeskTrackingLayout";
import {
  CANCELLATION_ACTION_LABELS,
  canShowRequestCancellation,
  isDeskCancellationRequest,
  parentActionLabel,
} from "../../../../utils/deskRequestCancellation";

const STATUS_TABS = ["Pending", "Approved", "Rejected", "Undo"];
const STATUS_TAB_LABELS = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Undo: "Recall",
};
export const ALL_DESK_TYPES = "All";
const canCreateRequestType = (typeLabel) => {
  if (!typeLabel || typeLabel === "Announcements" || typeLabel === ALL_DESK_TYPES) return false;
  const blocked = new Set([
    "Leave cancellation",
    ...CANCELLATION_ACTION_LABELS,
    "Confirmation",
    "Transfer",
    "Employee Onboarding",
    "Employee Onboarding Approval",
    "Onboarding Task",
    "Onboarding Letter Task",
    "Confirmation Approval",
    "Confirmation Task",
    "Confirmation Letter Task",
    "Transfer Approval",
    "Transfer Task",
    "Transfer Letter Task",
    "Exit / Separation Approval",
    "Resignation Approval",
    "Termination Approval",
    "Termination / Separation Approval",
    "Resignation Task",
    "Termination Task",
    "Resignation Letter Task",
    "Termination Letter Task",
  ]);
  return !blocked.has(typeLabel);
};

const lifecycleStageLabel = (request) => {
  if (!request) return "";
  if (request.deskMode === "checklist") return "Checklist task";
  if (request.deskMode === "request") {
    if (request.status === "Approved") return "Completed / approved";
    if (request.status === "Rejected") return "Rejected / cancelled";
    // Draft must not look like it is waiting on an approver.
    if (
      String(request.apiStatus || "").toLowerCase() === "draft" ||
      (!request.pendingRole &&
        !request.pendingLabel &&
        (!request.currentStep || Number(request.currentStep) === 0))
    ) {
      return "Draft — not submitted for approval";
    }
    return "Awaiting approval";
  }
  if (request.notifyOnlyFyi) return "Notification (FYI)";
  if (request.currentStep && request.totalSteps) {
    return `Approval · Step ${request.currentStep}/${request.totalSteps}`;
  }
  if (request.status === "Pending") {
    if (String(request.requestType || "").toLowerCase() === "cancellation") {
      return `${request.actionLabel || "Request"} pending`;
    }
    return "Pending approval";
  }
  return request.status || "";
};

const formatDeskDateTime = (value) => formatDeskDisplayDateTime(value);

const employeeNameCodeLine = (name, code) => {
  const n = String(name || "").trim();
  const c = String(code || "").trim();
  if (n && c) return `${n} (${c})`;
  return n || c || "";
};

const deskApprovalTrail = (request) => {
  const resolved = resolveWorkflowSteps(request);
  const steps = resolved.length
    ? resolved
    : Array.isArray(request?.workflowTrail)
      ? request.workflowTrail
      : Array.isArray(request?.workflowSteps)
        ? request.workflowSteps
        : [];
  if (steps.length) {
    const requester = String(request?.userName || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const raisedBy = String(request?.raisedByName || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const requestStatus = String(request?.status || "").trim().toLowerCase();
    const decided =
      requestStatus === "approved" ||
      requestStatus === "rejected" ||
      steps.some((s) => {
        const st = String(s?.status || "").toLowerCase();
        return st === "approved" || st === "rejected";
      });
    return steps.filter((step) => {
      const status = String(step?.status || "").trim().toLowerCase();
      if (decided && (status === "pending" || status === "current")) return false;
      if (status === "approved" || status === "rejected") return true;
      const actor = String(step?.actor || step?.displayText || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      if (requester && actor === requester) return false;
      if (raisedBy && requester && raisedBy === requester && actor === raisedBy) return false;
      return true;
    });
  }
  const approvals = Array.isArray(request?.approvals) ? request.approvals : [];
  return approvals.map((a, idx) => ({
    status:
      a.action === "approved" || a.status === "approved"
        ? "Approved"
        : a.action === "rejected" || a.status === "rejected"
          ? "Rejected"
          : "Pending",
    role: a.roleLabel || a.role,
    actor: a.approverName || a.actorName || a.roleLabel || a.role || "Approver",
    displayText: a.approverName || a.actorName || a.roleLabel || a.role || "Approver",
    actedAt: a.actedAt || null,
    reason: a.comments || a.reason || "",
    stepOrder: a.stepOrder ?? idx + 1,
  }));
};

const isOnboardingProfileRequest = (request) =>
  request?.actionLabel === ONBOARDING_APPROVAL_LABEL ||
  request?.actionLabel === ONBOARDING_REQUEST_LABEL ||
  (request?.lifecycleKind === "onboarding" && request?.deskMode === "request");

const isOnboardingDraftRequest = (request) => {
  if (!isOnboardingProfileRequest(request)) return false;
  if (String(request?.apiStatus || "").toLowerCase() === "draft") return true;
  // Fallback when apiStatus is missing but request is clearly not in workflow yet
  if (
    !request?.apiStatus &&
    request?.status === "Pending" &&
    !request?.pendingRole &&
    !request?.pendingLabel &&
    (!request?.currentStep || Number(request.currentStep) === 0)
  ) {
    return true;
  }
  return false;
};

const formatPendingWithLine = (request) => {
  if (isOnboardingDraftRequest(request)) {
    return "Not submitted — set Reporting Manager, then Submit for approval";
  }
  return workflowPendingDisplay(request);
};

const requestMatchesSearch = (request, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    request.userName,
    request.userEmail,
    request.employeeId,
    request.employeeCode,
    request.actionLabel,
    request.assigneeDisplay,
    request.pendingWith,
    request.policyName,
    request.formData?.policyName,
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const RequestStatusDeskModal = ({
  darkMode = false,
  panelKey,
  panelTypeItems = [],
  sortedPanelTypeItems = null,
  groupedTypeModules = [],
  typeBadgeCounts = {},
  statusCounts = {},
  selectedType,
  onSelectType,
  selectedStatus,
  onSelectStatus,
  onClose,
  onOpenForm,
  filteredRequests = [],
  selectedRequestId,
  onSelectRequest,
  onOpenStatusAction,
  onOpenBulkStatusAction,
  onOpenLinkedRecord,
  onContinueOnboardingDraft,
  /** "modal" = overlay popup; "page" = embedded in main content beside left rail */
  variant = "modal",
}) => {
  const isApprovalPanel = panelKey === "approvals";
  const showCreateAction = !isApprovalPanel && !!onOpenForm && canCreateRequestType(selectedType);
  const visibleStatus = STATUS_TABS.includes(selectedStatus)
    ? selectedStatus
    : selectedStatus === "Cancelled"
      ? "Approved"
      : "Pending";

  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [slaClock, setSlaClock] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setSlaClock(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const typeItemsWithAll = useMemo(
    () =>
      Array.isArray(sortedPanelTypeItems) && sortedPanelTypeItems.length > 0
        ? sortedPanelTypeItems
        : [ALL_DESK_TYPES, ...panelTypeItems],
    [sortedPanelTypeItems, panelTypeItems]
  );

  const moduleGroups = useMemo(() => {
    if (groupedTypeModules.length > 0) return groupedTypeModules;
    return [
      {
        module: "Other",
        types: typeItemsWithAll.filter((t) => t !== ALL_DESK_TYPES),
      },
    ];
  }, [groupedTypeModules, typeItemsWithAll]);

  const visibleRequests = useMemo(() => {
    const list = ensureUniqueDeskRequests(
      filteredRequests.filter((request) => requestMatchesSearch(request, itemSearchQuery))
    );
    return sortDeskRequestsLatestFirst(list);
  }, [filteredRequests, itemSearchQuery]);

  const selectedRequest =
    findDeskRequest(visibleRequests, selectedRequestId) ||
    findDeskRequest(filteredRequests, selectedRequestId);

  const visibleIdKey = useMemo(
    () => visibleRequests.map((request) => normalizeDeskRequestId(request.requestId)).join("|"),
    [visibleRequests]
  );

  /** Flat queue list (same card layout for Request and Approvals). */
  const queueSections = useMemo(() => {
    return [{ key: "_flat", label: null, items: visibleRequests }];
  }, [visibleRequests]);

  const selectedIdSet = useMemo(
    () => new Set(selectedRequestIds.map((id) => normalizeDeskRequestId(id))),
    [selectedRequestIds]
  );
  const allVisibleSelected =
    visibleRequests.length > 0 &&
    visibleRequests.every((request) =>
      selectedIdSet.has(normalizeDeskRequestId(request.requestId))
    );

  useEffect(() => {
    setSelectedRequestIds([]);
    setItemSearchQuery("");
  }, [selectedType, selectedStatus, panelKey]);

  useEffect(() => {
    setSelectedRequestIds((prev) => {
      const next = prev.filter((id) =>
        visibleRequests.some((request) => deskRequestIdsEqual(request.requestId, id))
      );
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
  }, [visibleIdKey, visibleRequests]);

  const toggleRequestSelection = (requestId) => {
    const id = normalizeDeskRequestId(requestId);
    if (!id) return;
    setSelectedRequestIds((prev) =>
      prev.some((item) => deskRequestIdsEqual(item, id))
        ? prev.filter((item) => !deskRequestIdsEqual(item, id))
        : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRequestIds((prev) =>
        prev.filter(
          (id) => !visibleRequests.some((request) => deskRequestIdsEqual(request.requestId, id))
        )
      );
      return;
    }
    const visibleIds = visibleRequests.map((request) =>
      normalizeDeskRequestId(request.requestId)
    );
    setSelectedRequestIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const bulkTargetIds =
    selectedRequestIds.length > 0
      ? selectedRequestIds
      : visibleRequests.map((request) => request.requestId);

  const canBulkApproveReject =
    isApprovalPanel && visibleStatus === "Pending" && selectedRequestIds.length > 0;
  const canBulkUndo =
    !isApprovalPanel && visibleStatus === "Pending" && selectedRequestIds.length > 0;

  const readOnlyInput =
    "mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-black dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100";
  const readOnlyLabel = "text-xs text-gray-700 dark:text-gray-300";

  const renderAppliedFormData = (request) => {
    const fd = request.formData || {};
    const summaryLabel = parentActionLabel(request.actionLabel) || request.actionLabel;
    if (isLeaveDeskRequest(request) || isLeaveActionLabel(request.actionLabel) || isLeaveActionLabel(summaryLabel)) {
      const duration = String(fd.leaveDuration || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const from = fd.fromDate || "";
      const to = fd.toDate || "";
      const sameDay = from && to && String(from).slice(0, 10) === String(to).slice(0, 10);
      const dateLine = sameDay
        ? formatDeskDisplayDate(from)
        : [from, to].filter(Boolean).map((d) => formatDeskDisplayDate(d)).filter(Boolean).join(" → ");
      return (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              {fd.leaveType || "Leave"}
            </span>
            {duration ? (
              <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                · {duration}
              </span>
            ) : null}
          </div>
          {dateLine ? (
            <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">{dateLine}</p>
          ) : null}
          {fd.reason ? (
            <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
              {fd.reason}
            </p>
          ) : null}
          {fd.notes ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Note: {fd.notes}
            </p>
          ) : null}
        </div>
      );
    }
    if (summaryLabel === "Restricted Holiday") {
      const holidayName = fd.holidayName || "Restricted Holiday";
      const dateLine = formatDeskDisplayDate(fd.date || fd.holidayDate);
      return (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              {holidayName}
            </span>
            <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              · Restricted Holiday
            </span>
          </div>
          {dateLine ? (
            <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">{dateLine}</p>
          ) : null}
          {fd.reason ? (
            <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
              {fd.reason}
            </p>
          ) : null}
          {fd.notes ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Note: {fd.notes}
            </p>
          ) : null}
        </div>
      );
    }
    if (summaryLabel === "Regularise checks") {
      const dateLine = formatDeskDisplayDate(fd.date);
      const inTime = fd.inTime || fd.checkInTime || fd.checkIn || "";
      const outTime = fd.outTime || fd.checkOutTime || fd.checkOut || "";
      const hours = fd.totalHours || "";
      const timeLine = [inTime, outTime].filter(Boolean).join(" → ");
      return (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              Regularise checks
            </span>
            {hours ? (
              <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                · {hours}
              </span>
            ) : null}
          </div>
          {dateLine ? (
            <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">{dateLine}</p>
          ) : null}
          {timeLine ? (
            <p className="mt-0.5 text-sm text-[hsl(var(--ds-fg))]">
              In {inTime || "—"} · Out {outTime || "—"}
            </p>
          ) : null}
          {fd.reason ? (
            <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
              {fd.reason}
            </p>
          ) : null}
          {fd.notes ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Note: {fd.notes}
            </p>
          ) : null}
        </div>
      );
    }
    if (request.actionLabel === "Comp Off Credit") {
      const dateLine = formatDeskDisplayDate(fd.date || fd.sourceDate);
      const hours = fd.hoursWorked != null ? `${fd.hoursWorked}h` : "";
      const days = fd.creditDays != null ? `${fd.creditDays} day(s)` : "";
      const meta = [hours, days].filter(Boolean).join(" · ");
      return (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              Comp Off Credit
            </span>
            {meta ? (
              <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                · {meta}
              </span>
            ) : null}
          </div>
          {dateLine ? (
            <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">Worked: {dateLine}</p>
          ) : null}
          {fd.reason ? (
            <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
              {fd.reason}
            </p>
          ) : null}
          {fd.notes ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Note: {fd.notes}
            </p>
          ) : null}
        </div>
      );
    }
    if (request.actionLabel === "Resignation") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Resignation Date
              <input
                readOnly
                value={formatDeskDisplayDate(fd.resignationDate) || fd.resignationDate || ""}
                className={readOnlyInput}
              />
            </label>
            <label className={readOnlyLabel}>
              Last Working Date
              <input
                readOnly
                value={formatDeskDisplayDate(fd.lastWorkingDate) || fd.lastWorkingDate || ""}
                className={readOnlyInput}
              />
            </label>
          </div>
          <label className={readOnlyLabel}>
            Reason Category
            <input readOnly value={fd.resignationReason || ""} className={readOnlyInput} />
          </label>
        </div>
      );
    }
    if (request.actionLabel === "Change Shift") {
      return (
        <div className="grid grid-cols-3 gap-2">
          <label className={readOnlyLabel}>
            Effective Date
            <input
              readOnly
              value={formatDeskDisplayDate(fd.date) || fd.date || ""}
              className={readOnlyInput}
            />
          </label>
          <label className={readOnlyLabel}>
            Current Shift
            <input readOnly value={fd.currentShift || ""} className={readOnlyInput} />
          </label>
          <label className={readOnlyLabel}>
            New Shift
            <input readOnly value={fd.shift || ""} className={readOnlyInput} />
          </label>
        </div>
      );
    }
    if (request.actionLabel === "Change WO") {
      return (
        <div className="grid grid-cols-2 gap-2">
          <label className={readOnlyLabel}>
            Current WO
            <input
              readOnly
              value={
                formatDeskDisplayDate(fd.date || fd.currentWoDate) ||
                fd.date ||
                fd.currentWoDate ||
                ""
              }
              className={readOnlyInput}
            />
          </label>
          <label className={readOnlyLabel}>
            Proposed WO
            <input
              readOnly
              value={
                formatDeskDisplayDate(fd.proposedDate || fd.proposedWoDate) ||
                fd.proposedDate ||
                fd.proposedWoDate ||
                ""
              }
              className={readOnlyInput}
            />
          </label>
        </div>
      );
    }
    if (request.actionLabel === "Ticket" || request.deskMode === "helpdesk") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Ticket No
              <input readOnly value={fd.ticketNo || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              Priority
              <input readOnly value={fd.ticketPriority || ""} className={readOnlyInput} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Type
              <input readOnly value={fd.ticketType || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              Category
              <input readOnly value={fd.ticketCategory || ""} className={readOnlyInput} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Subcategory
              <input readOnly value={fd.ticketSubcategory || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              Status
              <input readOnly value={fd.ticketStatus || ""} className={readOnlyInput} />
            </label>
          </div>
          <label className={readOnlyLabel}>
            Subject
            <input readOnly value={fd.ticketSubject || ""} className={readOnlyInput} />
          </label>
          <label className={readOnlyLabel}>
            Pending with
            <input readOnly value={fd.assignee || request.pendingWith || ""} className={readOnlyInput} />
          </label>
        </div>
      );
    }
    if (request.deskMode === "checklist" || /Task$/i.test(String(request.actionLabel || ""))) {
      const checklistName = getChecklistNameHighlight(request) || fd.taskName || "Onboarding task";
      const dueRaw = fd.dueAt;
      let dueLine = "";
      if (dueRaw) {
        const due = new Date(dueRaw);
        dueLine = Number.isNaN(due.getTime())
          ? String(dueRaw)
          : formatDeskDisplayDateTime(due);
      }
      const comments =
        fd.completedComments || fd.sentBackComments || fd.rejectedComments || "";
      return (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              {checklistName}
            </span>
            <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              · Checklist
            </span>
          </div>
          {dueLine ? (
            <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">Due {dueLine}</p>
          ) : null}
          {comments ? (
            <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
              {comments}
            </p>
          ) : null}
        </div>
      );
    }
    if (isOnboardingProfileRequest(request)) {
      const typeLine = fd.employmentType || "Onboarding";
      const orgLine = [fd.department, fd.designation, fd.branch]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" · ");
      const doj = formatDeskDisplayDate(fd.dateOfJoin);
      return (
        <div className="space-y-2">
          {isOnboardingDraftRequest(request) ? (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
              <p>
                This onboarding is still a <strong>draft</strong> (not in the
                approval queue). Set Reporting Manager, complete required tabs,
                then click <strong>Submit for approval</strong>.
              </p>
              {typeof onContinueOnboardingDraft === "function" ? (
                <button
                  type="button"
                  className="inline-flex items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-50 dark:hover:bg-amber-900/70"
                  onClick={() => onContinueOnboardingDraft(request)}
                >
                  Continue draft — set Reporting Manager
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">
                {typeLine}
              </span>
              {orgLine ? (
                <span className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  · {orgLine}
                </span>
              ) : null}
            </div>
            {doj ? (
              <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">Date of join {doj}</p>
            ) : null}
            {fd.reportingManager ? (
              <p className="mt-1 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                Reporting manager: {fd.reportingManager}
              </p>
            ) : null}
            {fd.policyName || request.policyName ? (
              <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Policy: {fd.policyName || request.policyName}
              </p>
            ) : null}
          </div>
        </div>
      );
    }
    if (
      request.source === "api" &&
      (request.lifecycleKind === "confirmation" ||
        request.lifecycleKind === "transfer" ||
        request.lifecycleKind === "separation" ||
        request.deskMode === "request")
    ) {
      const policyName =
        request.policyName || fd.policyName || request.formData?.policyName || "";
      const lifecycleFields = [
        ["Policy", policyName],
        ["Stage", lifecycleStageLabel(request)],
        ["Pending with", request.pendingWith || request.assigneeDisplay || ""],
        ["Employee", fd.employeeName || request.userName],
        ["Employee Code", fd.employeeCode || request.employeeCode || "-"],
        ["Expected confirmation", fd.expectedConfirmationDate],
        ["Probation end", fd.probationEndDate],
        ["Effective date", fd.effectiveDate || request.effectiveDate],
        ["Transfer type", fd.transferType || request.transferType],
        ["Separation type", fd.separationType || request.separationType],
        ["Separation date", fd.separationDate || request.separationDate],
        ["Last working day", fd.lastWorkingDay || request.lastWorkingDay],
        ["Workflow", fd.workflowKey || request.formData?.workflowKey],
      ];
      const lifecycleDateLabels = new Set([
        "Expected confirmation",
        "Probation end",
        "Effective date",
        "Separation date",
        "Last working day",
      ]);
      return (
        <div className="grid grid-cols-2 gap-2">
          {lifecycleFields
            .filter(([, value]) => value != null && String(value).trim() !== "")
            .map(([label, value]) => (
              <label key={label} className={readOnlyLabel}>
                {label}
                <input
                  readOnly
                  value={
                    lifecycleDateLabels.has(label)
                      ? formatDeskDisplayDate(value) || String(value)
                      : String(value)
                  }
                  className={readOnlyInput}
                />
              </label>
            ))}
        </div>
      );
    }
    if (
      request.actionLabel === "Tax Planning" ||
      request.actionLabel === "Tax Declaration" ||
      request.actionLabel === "Investment Declaration"
    ) {
      const formatDeskValue = (value) => {
        if (value == null || value === "") return "";
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
          return String(value);
        }
        if (Array.isArray(value)) {
          return value
            .map((row) =>
              row && typeof row === "object"
                ? Object.entries(row)
                    .filter(([, v]) => v != null && String(v).trim() !== "")
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ")
                : String(row)
            )
            .filter(Boolean)
            .join(" | ");
        }
        if (typeof value === "object") {
          return Object.entries(value)
            .filter(([, v]) => v != null && String(v).trim() !== "" && typeof v !== "object")
            .map(([k, v]) => `${k}: ${v}`)
            .join("; ");
        }
        return String(value);
      };
      const rows = [
        ["Financial Year", fd.financialYear],
        ["Tax Regime", fd.taxRegime],
        ["Status", fd.status],
        ["Previous Employer", fd.prevEmployerName],
        ["Prev Employer PAN", fd.prevEmployerPan],
        ["Prev Gross Salary", fd.prevGrossSalary],
        ["Prev Tax Deducted", fd.prevTaxDeducted],
        ["Prev Professional Tax", fd.prevProfessionalTax],
        ["HRA", fd.hra || formatDeskValue(fd.hraEntries || fd.houseRent)],
        ["Section 80C", fd.section80C ? formatDeskValue(fd.section80C) : ""],
        ["VIA Deductions", fd.sectionVIA ? formatDeskValue(fd.sectionVIA) : ""],
        [
          "House Property",
          fd.houseProperty
            ? formatDeskValue(fd.houseProperty)
            : formatDeskValue(fd.housePropertyEntries),
        ],
        ["Note", fd.note],
      ].filter(([, value]) => value != null && String(value).trim() !== "");
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2">
            {rows.map(([label, value]) => (
              <label key={label} className={readOnlyLabel}>
                {label}
                <textarea
                  readOnly
                  rows={String(value).length > 80 ? 3 : 1}
                  value={String(value)}
                  className={readOnlyInput}
                />
              </label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-1 text-[11px] text-gray-700 dark:text-gray-300">
        {Object.entries(fd)
          .filter(([, value]) => {
            if (value == null) return false;
            if (typeof value === "object") return true;
            return String(value).trim() !== "";
          })
          .map(([key, value]) => {
            let display = "";
            if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
              display = String(value);
            } else if (Array.isArray(value)) {
              display = value
                .map((row) =>
                  row && typeof row === "object" ? JSON.stringify(row) : String(row)
                )
                .join(" | ");
            } else if (typeof value === "object") {
              display = Object.entries(value)
                .filter(([, v]) => v != null && typeof v !== "object")
                .map(([k, v]) => `${k}: ${v}`)
                .join("; ");
            } else {
              display = String(value);
            }
            if (!display.trim()) return null;
            return (
              <div key={key} className="flex justify-between gap-3">
                <span className="font-medium text-gray-500 dark:text-gray-400">{key}</span>
                <span className="max-w-[70%] text-right break-words">{display}</span>
              </div>
            );
          })}
      </div>
    );
  };

  const isChecklistRequest = selectedRequest?.deskMode === "checklist";
  const isOnboardingDesk =
    selectedRequest && isOnboardingProfileRequest(selectedRequest);
  const canActOnOnboardingRequest =
    isOnboardingDesk &&
    selectedRequest.status === "Pending" &&
    (selectedRequest.canApprove === true ||
      selectedRequest.canAct === true ||
      selectedRequest.deskMode === "approval");
  const profileBlocksApproval =
    selectedRequest &&
    selectedRequest.profileComplete === false &&
    (isChecklistRequest ||
      selectedRequest.actionLabel === ONBOARDING_APPROVAL_LABEL ||
      selectedRequest.actionLabel === ONBOARDING_REQUEST_LABEL ||
      (isOnboardingDesk && canActOnOnboardingRequest));

  const actionButtons = (() => {
    if (!selectedRequest) return [];
    // Prefer shared matrix (includes Submit for approval on drafts).
    const matrix = getDeskActionButtons(selectedRequest);
    if (matrix.length) return matrix;
    // My requests fallback: Approve/Reject when can act; else Recall.
    if (!isApprovalPanel) {
      if (canActOnOnboardingRequest) {
        return [
          { status: "Approved", label: "Approve" },
          { status: "Rejected", label: "Reject" },
        ];
      }
      return [{ status: "Undo", label: "Recall" }];
    }
    if (isChecklistRequest) {
      return [
        { status: "Approved", label: "Done" },
        { status: "Rejected", label: "Reject" },
      ];
    }
    return [
      { status: "Approved", label: "Approve" },
      { status: "Rejected", label: "Reject" },
      { status: "Undo", label: "Recall" },
    ];
  })();

  const isPage = variant === "page";
  const showTypeBadges = isApprovalPanel;
  const queueCount = visibleRequests.length;

  const selectType = (typeLabel) => {
    onSelectType(typeLabel || ALL_DESK_TYPES);
  };

  const filterSelectCls =
    "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 py-1.5 text-xs text-[hsl(var(--ds-fg))]";

  const renderQueueCard = (request, index) => {
    const rowId = normalizeDeskRequestId(request.requestId) || `desk-row-${index}`;
    const isRowSelected = deskRequestIdsEqual(selectedRequestId, request.requestId);
    const isChecked = selectedIdSet.has(rowId);
    const typeTitle = request.actionLabel || "Request";
    const actionKind = resolveDeskActionKind(request);
    const isChecklistKind = actionKind === "checklist" || actionKind === "letter";
    const checklistName = getChecklistNameHighlight(request);
    const titleText =
      isChecklistKind && checklistName && checklistName !== typeTitle
        ? `${typeTitle} - ${checklistName}`
        : typeTitle;
    const kindBadgeLabel =
      actionKind === "letter" ? "Letter" : actionKind === "checklist" ? "Checklist" : null;
    const nameLine = [
      request.userName,
      request.employeeCode || request.formData?.employeeCode
        ? `(${request.employeeCode || request.formData?.employeeCode})`
        : null,
    ]
      .filter(Boolean)
      .join(" ");
    const sla = resolveDeskSlaStatus(request, slaClock);

    const selectRow = () => {
      if (typeof onSelectRequest === "function") onSelectRequest(request.requestId);
    };

    return (
      <div
        role="option"
        aria-selected={isRowSelected}
        onClick={selectRow}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            selectRow();
          }
        }}
        tabIndex={0}
        className={`relative flex min-h-[52px] cursor-pointer items-center gap-2 rounded-xl border px-2.5 py-2 pr-14 transition ${
          isRowSelected
            ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/5 shadow-sm"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]/50"
        }`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            event.stopPropagation();
            toggleRequestSelection(request.requestId);
          }}
          className="shrink-0 rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
          aria-label={`Select ${titleText} for bulk action`}
        />
        <div className="min-w-0 flex-1 text-left">
          <div className="flex min-w-0 items-center gap-1.5">
            <div
              className="min-w-0 truncate text-sm font-semibold text-[hsl(var(--ds-fg))]"
              title={titleText}
            >
              {titleText}
            </div>
            {kindBadgeLabel ? (
              <DsBadge
                tone={actionKind === "letter" ? "warning" : "info"}
                className="shrink-0 !px-1.5 !py-0 !text-[9px] !leading-4"
              >
                {kindBadgeLabel}
              </DsBadge>
            ) : null}
          </div>
          {nameLine ? (
            <div className="ds-h3 mt-0.5 truncate !text-[9px] !font-medium text-[hsl(var(--ds-muted-fg))]">
              {nameLine}
            </div>
          ) : null}
          {leaveLinkedCancellationCaption(request.linkedCancellation) ? (
            <div
              className={`mt-0.5 truncate text-[10px] font-medium ${
                String(request.linkedCancellation?.status || "").toLowerCase() ===
                "approved"
                  ? "text-[hsl(var(--ds-danger))]"
                  : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              {leaveLinkedCancellationCaption(request.linkedCancellation)}
            </div>
          ) : null}
        </div>
        {sla ? (
          <span
            className={`pointer-events-none absolute right-2 top-2 text-[10px] font-semibold leading-none ${sla.className}`}
            title={sla.title || `SLA: ${sla.label}`}
          >
            {sla.label}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={
        isPage
          ? "fixed bottom-0 right-0 z-[10010] flex bg-[hsl(var(--ds-bg))]"
          : "fixed inset-0 z-[10015] flex items-center justify-center bg-black/45 p-4"
      }
      style={
        isPage
          ? {
              left: "var(--admin-left-sidebar-width, 0px)",
              top: "calc(5rem + var(--admin-secondary-nav-height, 0px))",
            }
          : undefined
      }
    >
      <div
        className={
          isPage
            ? "flex h-full w-full flex-col overflow-hidden bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            : "flex h-[78vh] w-[92.2rem] max-w-[96vw] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-black shadow-xl dark:border-[#3C3C4C] dark:bg-[#121212] dark:text-gray-100"
        }
      >
        <div className="shrink-0 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {!isPage ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden p-4 lg:grid-cols-[minmax(22rem,32rem)_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/20 p-2">
            {/* Type · Status — same column width as the list */}
            <div className="mb-2 flex flex-col gap-2">
              <label className="block">
                <span className="sr-only">Type</span>
                <select
                  value={selectedType || ALL_DESK_TYPES}
                  onChange={(e) => selectType(e.target.value)}
                  className={`${filterSelectCls} w-full`}
                  aria-label="Type"
                  title="Filter by type"
                >
                  <option value={ALL_DESK_TYPES}>All types</option>
                  {moduleGroups.map((g) => (
                    <optgroup key={g.module} label={g.module}>
                      {g.types.map((t) => (
                        <option key={t} value={t}>
                          {t}
                          {showTypeBadges ? ` (${typeBadgeCounts?.[t] ?? 0})` : ""}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>

              <div
                className="grid w-full grid-cols-4 gap-1"
                role="tablist"
                aria-label="Status"
              >
                {STATUS_TABS.map((status) => {
                  const active = visibleStatus === status;
                  const statusCount = statusCounts?.[status];
                  const showStatusCount =
                    statusCount != null && Number.isFinite(statusCount);
                  return (
                    <button
                      key={status}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => onSelectStatus(status)}
                      title={`${STATUS_TAB_LABELS[status] || status}${
                        showStatusCount ? ` (${statusCount})` : ""
                      }`}
                      className={`min-w-0 rounded-md border bg-transparent px-0.5 py-1 text-center text-[8px] font-semibold leading-tight transition ${
                        active
                          ? isApprovalPanel
                            ? "border-[hsl(var(--ds-secondary))] text-[hsl(var(--ds-secondary))]"
                            : "border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]"
                          : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-fg))]/40"
                      }`}
                    >
                      <span className="block whitespace-nowrap">
                        {STATUS_TAB_LABELS[status] || status}
                        {showStatusCount ? `(${statusCount})` : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {showCreateAction ? (
              <div className="mb-2 flex justify-start">
                <button
                  type="button"
                  onClick={onOpenForm}
                  className="rounded-lg bg-[hsl(var(--ds-primary))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--ds-primary-fg))] hover:opacity-95"
                >
                  New {selectedType}
                </button>
              </div>
            ) : null}
            <div className="relative mb-2">
              <Search
                size={13}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <input
                type="search"
                value={itemSearchQuery}
                onChange={(e) => setItemSearchQuery(e.target.value)}
                placeholder="Search name, email, employee ID…"
                className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-2 pl-8 pr-2 text-[11px] text-[hsl(var(--ds-fg))] placeholder:text-[hsl(var(--ds-muted-fg))]"
              />
            </div>

            {visibleRequests.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] pb-2">
                <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px]">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                  />
                  Select all
                  {selectedRequestIds.length > 0 ? (
                    <span className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      ({selectedRequestIds.length})
                    </span>
                  ) : null}
                </label>
                <div className="inline-flex flex-wrap gap-2">
                  {canBulkApproveReject && onOpenBulkStatusAction ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onOpenBulkStatusAction(selectedRequestIds, "Approved")}
                        className="rounded-lg border border-green-300 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-800"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenBulkStatusAction(selectedRequestIds, "Rejected")}
                        className="rounded-lg border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-800"
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                  {canBulkUndo && !isApprovalPanel && onOpenBulkStatusAction ? (
                    <button
                      type="button"
                      onClick={() => onOpenBulkStatusAction(selectedRequestIds, "Undo")}
                      className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                    >
                      Recall
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
              {queueSections.map((section) => (
                <div key={section.key} className="space-y-1.5">
                  {section.label ? (
                    <button
                      type="button"
                      onClick={() => selectType(section.label)}
                      className="sticky top-0 z-[1] flex w-full items-center justify-between rounded-lg bg-[hsl(var(--ds-muted))] px-2 py-1.5 text-left"
                      title={`Focus ${section.label}`}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                        {section.label}
                      </span>
                      <span className="rounded-full bg-[hsl(var(--ds-primary))] px-1.5 text-[10px] font-semibold text-[hsl(var(--ds-primary-fg))]">
                        {section.items.length}
                      </span>
                    </button>
                  ) : null}
                  {section.items.map((request, index) => (
                    <div key={normalizeDeskRequestId(request.requestId) || `desk-row-${index}`}>
                      {renderQueueCard(request, index)}
                    </div>
                  ))}
                </div>
              ))}
              {visibleRequests.length === 0 && (
                <div className="rounded-xl border border-dashed border-[hsl(var(--ds-border))] px-3 py-8 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
                  {itemSearchQuery.trim()
                    ? "No items match your search."
                    : isApprovalPanel
                    ? "You're clear — nothing waiting for your action."
                    : selectedType === "Resignation"
                    ? "No resignation requests yet. Use New Resignation above to raise one."
                    : "No items for this filter. Change Type or status above."}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
              {!selectedRequest ? (
                <div className="flex h-full min-h-[240px] items-center justify-center px-4 text-center text-xs text-gray-500 dark:text-gray-400">
                  {isApprovalPanel
                    ? "Select an approval to review the details and take action."
                    : `Select a request from the list to view status${
                        showCreateAction
                          ? `, or click "${selectedType}" to create a new request.`
                          : ". Lifecycle items are raised from Employee — track them here."
                      }`}
                </div>
              ) : (
                <div className="flex h-full min-h-0 flex-col">
                  <div className="border-b border-[hsl(var(--ds-border))] pb-3">
                    <div className="text-base font-semibold text-[hsl(var(--ds-fg))]">
                      {selectedRequest.actionLabel}
                    </div>
                    <p className="ds-h3 mt-1 !text-[14px] !font-medium text-[hsl(var(--ds-muted-fg))]">
                      {typeof onOpenLinkedRecord === "function" &&
                      (selectedRequest.linkedEmployeeId ||
                        selectedRequest.employeeCode) ? (
                        <button
                          type="button"
                          title="Open employee profile (read-only)"
                          onClick={() => onOpenLinkedRecord(selectedRequest)}
                          className="text-left underline-offset-2 hover:underline"
                        >
                          {employeeNameCodeLine(
                            selectedRequest.formData?.employeeName ||
                              selectedRequest.userName,
                            selectedRequest.formData?.employeeCode ||
                              selectedRequest.employeeCode
                          ) || selectedRequest.userName || "Employee"}
                        </button>
                      ) : (
                        employeeNameCodeLine(
                          selectedRequest.formData?.employeeName ||
                            selectedRequest.userName,
                          selectedRequest.formData?.employeeCode ||
                            selectedRequest.employeeCode
                        ) || selectedRequest.userName || "Employee"
                      )}
                    </p>
                    {getChecklistNameHighlight(selectedRequest) ? (
                      <div className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        Checklist:{" "}
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-[hsl(var(--ds-fg))] dark:bg-amber-900/40 dark:text-amber-100">
                          {getChecklistNameHighlight(selectedRequest)}
                        </span>
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      <span>
                        {lifecycleStageLabel(selectedRequest) || selectedRequest.status}
                      </span>
                      {selectedRequest.status === "Pending" &&
                      formatPendingWithLine(selectedRequest) ? (
                        <span className="rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                          Pending · {formatPendingWithLine(selectedRequest)}
                        </span>
                      ) : null}
                      <span aria-hidden>·</span>
                      <span>{formatDeskDateTime(selectedRequest.submittedAt)}</span>
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-3 pr-1">
                    {isOnboardingProfileRequest(selectedRequest) ? (
                      <div>{renderAppliedFormData(selectedRequest)}</div>
                    ) : isLeaveDeskRequest(selectedRequest) ||
                      isLeaveActionLabel(selectedRequest.actionLabel) ? (
                      <LeaveDeskTrackingLayout
                        formData={selectedRequest.formData || {}}
                        leaveTrailSteps={deskApprovalTrail(selectedRequest)}
                        linkedCancellation={selectedRequest.linkedCancellation}
                        pendingHint={
                          selectedRequest.status === "Pending" &&
                          formatPendingWithLine(selectedRequest) ? (
                            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                              Awaiting action from {formatPendingWithLine(selectedRequest)}.
                            </p>
                          ) : null
                        }
                      />
                    ) : (
                      <DeskRequestTrackingLayout
                        facts={renderAppliedFormData(selectedRequest)}
                        trailSteps={deskApprovalTrail(selectedRequest)}
                        pendingHint={
                          selectedRequest.status === "Pending" &&
                          formatPendingWithLine(selectedRequest) ? (
                            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                              Awaiting action from {formatPendingWithLine(selectedRequest)}.
                            </p>
                          ) : null
                        }
                      />
                    )}
                  </div>
                  <div className="mt-2 flex flex-col gap-2 border-t border-[hsl(var(--ds-border))] pt-2">
                    <div className="flex flex-wrap justify-end gap-2">
                    {selectedRequest.notifyOnlyFyi && selectedRequest.status === "Pending" ? (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenStatusAction(selectedRequest.requestId, "Acknowledged")
                        }
                        className="rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        No action required
                      </button>
                    ) : (
                      <>
                      {!(
                        selectedRequest.readOnlyApproval ||
                        selectedRequest.notifyOnlyFyi ||
                        selectedRequest.status !== "Pending"
                      ) &&
                      actionButtons
                        .filter(({ status }) => {
                          if (!isApprovalPanel) {
                            if (status === "Submitted") {
                              return (
                                String(selectedRequest.apiStatus || "").toLowerCase() ===
                                "draft"
                              );
                            }
                            if (canActOnOnboardingRequest) {
                              return status === "Approved" || status === "Rejected";
                            }
                            return status === "Undo" && selectedRequest.status === "Pending";
                          }
                          return (
                            status === "Approved" ||
                            status === "Rejected" ||
                            status === "Submitted" ||
                            status === "Query"
                          );
                        })
                        .map(({ status, label }) => {
                          const approveBlocked =
                            status === "Approved" && profileBlocksApproval;
                          return (
                        <button
                          key={`${selectedRequest.requestId}-${status}`}
                          type="button"
                          disabled={approveBlocked}
                          title={
                            approveBlocked
                              ? "Approve unavailable until required profile tabs are complete"
                              : undefined
                          }
                          onClick={() => {
                            if (approveBlocked) return;
                            onOpenStatusAction(selectedRequest.requestId, status);
                          }}
                          className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${
                            approveBlocked
                              ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-[#3C3C4C] dark:bg-[#1a1a1a] dark:text-gray-500"
                              : status === "Approved" || status === "Submitted"
                              ? "border-[hsl(var(--ds-success))]/40 bg-[hsl(var(--ds-success))]/10 text-green-800 hover:bg-green-100 dark:border-[hsl(var(--ds-success))]/40 dark:bg-[hsl(var(--ds-success))]/15 dark:text-green-200"
                              : status === "Rejected"
                              ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
                              : status === "Query"
                              ? "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-900/30 dark:text-sky-200"
                              : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                          }`}
                        >
                          {label}
                        </button>
                          );
                        })}
                      {canShowRequestCancellation(selectedRequest) ? (
                        <button
                          type="button"
                          onClick={() =>
                            onOpenStatusAction(
                              selectedRequest.requestId,
                              "CancelRequested"
                            )
                          }
                          className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                        >
                          Request cancellation
                        </button>
                      ) : null}
                      {(isLeaveDeskRequest(selectedRequest) ||
                        (!isDeskCancellationRequest(selectedRequest) &&
                          selectedRequest.status === "Approved")) &&
                      selectedRequest.openCancellationRequestId ? (
                        <p className="w-full text-right text-[11px] text-[hsl(var(--ds-muted-fg))]">
                          Cancellation is already pending
                        </p>
                      ) : null}
                      </>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>
  );
};

export default RequestStatusDeskModal;
